import React, { Component } from "react";
import axios from "axios";
import debounce from "./utility/debounce"; // Adjust path if necessary
const config = require("../config.json"); // Assuming it's in the parent directory relative to src

class SearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      language: props.initialLanguage || "da", // Default to Danish or use prop
      results: [],
      isLoading: false,
      error: null,
      // To show some message while typing or if no results
      searchStatus: "", // e.g., 'Typing...', 'No results found', 'Error fetching results'
    };

    // Debounce the API call function
    // Adjust the delay (in ms) as needed, 300-500ms is common
    this.debouncedSearch = debounce(this.performSearch, 500);
  }

  handleInputChange = (event) => {
    const newQuery = event.target.value;
    this.setState({
      query: newQuery,
      isLoading: true,
      searchStatus: "Typing...",
    });

    // Call the debounced search function
    // If query is empty, cancel search or clear results
    if (newQuery.trim() === "") {
      clearTimeout(this.debouncedSearch); // Clear any pending search
      this.setState({
        results: [],
        isLoading: false,
        searchStatus: "",
        error: null,
      });
      if (this.props.onResults) {
        this.props.onResults([]); // Clear results in parent
      }
    } else {
      this.debouncedSearch(newQuery, this.state.language);
    }
  };

  handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    this.setState({ language: newLanguage }, () => {
      // Optionally, re-run search if there's a query
      if (this.state.query.trim() !== "") {
        this.performSearch(this.state.query, newLanguage);
      }
    });
  };

  searchForCategory = async (category) => {
    this.setState({
      isLoading: true,
      error: null,
      searchStatus: "Searching...",
    });

    try {
      const apiUrl = `${config.api.invokeUrl}/cookbook/categorysearch`; // Your API Gateway search endpoint

      const response = await axios.get(apiUrl, {
        params: {
          category,
        },
      });

      if (response.data && response.data.results) {
        this.setState({
          results: response.data.results,
          isLoading: false,
          searchStatus:
            response.data.results.length === 0
              ? `No results found for "${query}"`
              : "",
        });
        if (this.props.onResults) {
          this.props.onResults(response.data.results); // Pass results to parent
        }
      } else {
        this.setState({
          results: [],
          isLoading: false,
          searchStatus: `No results found for "${query}"`,
        });
        if (this.props.onResults) {
          this.props.onResults([]);
        }
      }
    } catch (err) {
      console.error("Error fetching search results:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch results";
      this.setState({
        error: errorMessage,
        isLoading: false,
        results: [],
        searchStatus: `Error: ${errorMessage}`,
      });
      if (this.props.onResults) {
        this.props.onResults([]); // Clear results on error
      }
    }
  };

  performSearch = async (query, language) => {
    if (query.trim() === "") {
      this.setState({
        results: [],
        isLoading: false,
        searchStatus: "",
        error: null,
      });
      if (this.props.onResults) {
        this.props.onResults([]);
      }
      return;
    }

    this.setState({
      isLoading: true,
      error: null,
      searchStatus: "Searching...",
    });

    try {
      const apiUrl = `${config.api.invokeUrl}/cookbook/search`; // Your API Gateway search endpoint

      const response = await axios.get(apiUrl, {
        params: {
          q: query,
          lang: language,
          // type: 'Recipe' // Optionally add type filter if needed by default
        },
      });

      if (response.data && response.data.results) {
        this.setState({
          results: response.data.results,
          isLoading: false,
          searchStatus:
            response.data.results.length === 0
              ? `No results found for "${query}"`
              : "",
        });
        if (this.props.onResults) {
          this.props.onResults(response.data.results); // Pass results to parent
        }
      } else {
        this.setState({
          results: [],
          isLoading: false,
          searchStatus: `No results found for "${query}"`,
        });
        if (this.props.onResults) {
          this.props.onResults([]);
        }
      }
    } catch (err) {
      console.error("Error fetching search results:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch results";
      this.setState({
        error: errorMessage,
        isLoading: false,
        results: [],
        searchStatus: `Error: ${errorMessage}`,
      });
      if (this.props.onResults) {
        this.props.onResults([]); // Clear results on error
      }
    }
  };

  render() {
    const { query, language, isLoading, searchStatus, error, results } =
      this.state;

    return (
      <div className="search-bar-container">
        <div className="field has-addons">
          <div className="control">
            <div className="select">
              <select
                value={language}
                onChange={this.handleLanguageChange}
                aria-label="Select language"
              >
                <option value="da">Dansk</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
          <div className="control is-expanded">
            <input
              className={`input ${isLoading ? "is-loading" : ""}`}
              type="text"
              placeholder="Search recipes and ingredients..."
              value={query}
              onChange={this.handleInputChange}
              aria-label="Search input"
            />
          </div>
          {/* Optionally, add a search button if you don't want it to be purely on type */}
          {/* <div className="control">
            <button
              className={`button is-info ${isLoading ? 'is-loading' : ''}`}
              onClick={() => this.performSearch(query, language)} // Non-debounced search on click
            >
              Search
            </button>
          </div> */}
        </div>

        {isLoading && query && (
          <p className="help is-info">{searchStatus || "Loading..."}</p>
        )}
        {!isLoading && error && (
          <p className="help is-danger">{searchStatus}</p>
        )}
        {!isLoading && !error && searchStatus && query && (
          <p className="help">{searchStatus}</p>
        )}

        {/*
          You'll likely want to render results in a parent component or a separate ResultsList component.
          This SearchBar component can pass the results up via a prop (e.g., this.props.onResults).
          For demonstration, we can list some basic info here:
        */}
        {/* <div className="search-results-preview" style={{ marginTop: '1rem' }}>
          {results.length > 0 && (
            <ul>
              {results.slice(0, 5).map(result => ( // Preview first 5 results
                <li key={result.id || result.pk}>
                  <strong>{result.name || result.name_en}</strong> ({result.entityType}) - Score: {result.score?.toFixed(2)}
                  {result.highlight && result.highlight.name && (
                     <p style={{fontSize: '0.8em'}} dangerouslySetInnerHTML={{ __html: `Highlight: ${result.highlight.name[0]}` }} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div> */}
        {this.props.recipeCategories.map((recCat) => (
          <button
            onClick={async () => {
              await this.searchForCategory(recCat);
            }}
          >
            {recCat}
          </button>
        ))}
      </div>
    );
  }
}

export default SearchBar;
